<script setup lang="ts">
import { onMounted, watch, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useCreateSensorStore } from '@/store/createSensor' 
import { ISensorFormData } from '@/interfaces/components/createSensor/create-sensor.ts'
import ConnectionFeedback from '@/components/Sensors/Create/ConnectionFeedback.vue'

const props = defineProps<{
  modelValue: ISensorFormData
}>()

const emit = defineEmits(['update:modelValue', 'validate'])

const createStore = useCreateSensorStore()
const { connectionStatus, connectionPayload } = storeToRefs(createStore)
const { verifyConnection, resetState, disconnectSensor } = createStore

onMounted(() => {
  if (connectionStatus.value === 'success') {
    emit('validate', true)
  } else {
    handleRetry()
  }
})

onUnmounted(() => {
  disconnectSensor()
})

watch(connectionStatus, (newStatus) => {
  if (newStatus === 'loading') {
    emit('validate', false)
  } else if (newStatus === 'success') {
    emit('validate', true)
  } else if (newStatus === 'error') {
    emit('validate', false)
  }
})

const handleRetry = () => {
  verifyConnection(props.modelValue.subid)
}
</script>

<template>
  <div class="w-full h-full flex flex-col items-center justify-center min-h-[300px]">
    
    <div v-if="connectionStatus === 'loading'" class="flex flex-col items-center animate-pulse">
      <div class="mb-8">
        <svg class="h-16 w-16 text-[#1d4ed8] animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-black dark:text-white">Conectando ao Sensor...</h3>
      <p class="text-sm text-slate-500 mt-2">Verificando SubID: {{ modelValue.subid }}</p>
    </div>

    <ConnectionFeedback 
      v-else-if="connectionStatus === 'success'"
      type="success"
      title="Conexão Confirmada"
      message="Dados de sensor recebidos com sucesso."
    >
      <div class="mt-6 w-full max-w-md text-left bg-slate-100 p-4 border border-slate-200 shadow-inner">
        <pre class="text-xs text-slate-700 font-mono overflow-x-auto">{{ JSON.stringify(connectionPayload, null, 2) }}</pre>
      </div>
    </ConnectionFeedback>

    <ConnectionFeedback 
      v-else-if="connectionStatus === 'error'"
      type="error"
      title="Sem Resposta do Sensor"
      message="O sistema não recebeu nenhum dado do sensor. O payload retornou vazio ou nulo."
    >
      <div class="flex flex-col gap-2 mt-4">
        <p class="text-xs text-slate-500">Verifique a conexão de rede do dispositivo.</p>
        <button 
          @click="handleRetry"
          class="mt-2 px-6 py-2 text-sm font-medium text-[#1d4ed8] bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          Tentar Novamente
        </button>
      </div>
    </ConnectionFeedback>

  </div>
</template>